import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Trash2, Loader2, AlertTriangle, ShieldAlert, Sparkles, Download, MessagesSquare } from "lucide-react";
import { AdminChat } from "@/components/AdminMessages";
import { DateField } from "@/components/DateField";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const ROLE = { super_admin: "Super Admin", business: "Maestro", customer: "Alumno" };
const LABELS = {
  perfil_maestro: "Perfil de Maestro", talleres: "Talleres", sesiones: "Sesiones", favoritos: "Favoritos",
  preguntas: "Preguntas", calificaciones: "Calificaciones", imagenes: "Imágenes", mensajes_contacto: "Mensajes de contacto",
  reservas_a_anonimizar: "Reservas (se anonimizan, no se borran)",
};

function DeleteDialog({ target, onClose, onDeleted }) {
  const [p, setP] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/admin/users/${target.id}/deletion-preview`).then((r) => setP(r.data))
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); onClose(); });
  }, [target.id]); // eslint-disable-line
  const match = confirm.trim().toLowerCase() === (target.email || "").toLowerCase();
  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${target.id}/delete`, { confirm_email: confirm.trim() });
      toast.success(`${target.name || target.email} y todos sus datos fueron eliminados`);
      onDeleted();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="delete-user-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive"><AlertTriangle className="w-5 h-5" />Eliminar usuario y todos sus datos</DialogTitle>
          <DialogDescription>Esta acción es permanente y no se puede deshacer.</DialogDescription>
        </DialogHeader>
        {!p ? <div className="py-8 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div> : (
          <div className="space-y-4">
            <div className="rounded-lg bg-muted/60 p-3 text-sm">
              <p className="font-semibold">{p.user.name || "Sin nombre"} <Badge variant="outline" className="ml-1">{ROLE[p.user.role] || p.user.role}</Badge></p>
              <p className="text-muted-foreground">{p.user.email}{p.user.business_name ? ` · ${p.user.business_name}` : ""}</p>
            </div>
            <div>
              <p className="text-sm font-medium mb-1.5">Se eliminará:</p>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm" data-testid="delete-user-counts">
                {Object.entries(p.counts).map(([k, v]) => (
                  <li key={k} className={`flex justify-between gap-2 ${k === "reservas_a_anonimizar" ? "col-span-2" : ""}`}>
                    <span className="text-muted-foreground">{LABELS[k] || k}</span><span className="font-semibold tabular-nums">{v}</span>
                  </li>
                ))}
              </ul>
            </div>
            {p.blockers.length > 0 ? (
              <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm space-y-1" data-testid="delete-user-blockers">
                <p className="font-semibold text-destructive flex items-center gap-1.5"><ShieldAlert className="w-4 h-4" />No se puede eliminar todavía</p>
                {p.blockers.map((b) => <p key={b} className="text-muted-foreground">{b}</p>)}
              </div>
            ) : (
              <div>
                <Label>Para confirmar, escribe el correo del usuario</Label>
                <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={target.email} autoComplete="off" data-testid="delete-user-confirm-input" />
              </div>
            )}
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="delete-user-cancel">Cancelar</Button>
          <Button variant="destructive" onClick={run} disabled={!p?.can_delete || !match || busy} data-testid="delete-user-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-1.5" />Eliminar definitivamente</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const clDay = (d) => d.toLocaleDateString("en-CA", { timeZone: "America/Santiago" });
const daysAgo = (n) => clDay(new Date(Date.now() - n * 864e5));
const PRESETS = [
  { id: "new", label: "Nuevos (7 días)", from: () => daysAgo(6), to: () => clDay(new Date()) },
  { id: "30", label: "Últimos 30 días", from: () => daysAgo(29), to: () => clDay(new Date()) },
  { id: "month", label: "Este mes", from: () => `${clDay(new Date()).slice(0, 8)}01`, to: () => clDay(new Date()) },
];

function WhatsAppIcon() {
  return <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true"><path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.62.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35zM12.04 21.5h-.01a9.45 9.45 0 0 1-4.82-1.32l-.35-.21-3.58.94.96-3.49-.23-.36a9.43 9.43 0 0 1-1.45-5.03c0-5.22 4.25-9.46 9.48-9.46 2.53 0 4.91.99 6.7 2.78a9.4 9.4 0 0 1 2.77 6.7c0 5.22-4.25 9.46-9.47 9.46zm8.06-17.52A11.32 11.32 0 0 0 12.04.64C5.76.64.65 5.75.65 12.03c0 2.01.52 3.97 1.52 5.7L.55 23.64l6.05-1.59a11.36 11.36 0 0 0 5.44 1.39h.01c6.28 0 11.39-5.11 11.39-11.39 0-3.04-1.18-5.9-3.34-8.07z" /></svg>;
}

function WaLink({ u }) {
  const digits = (u.phone || "").replace(/\D/g, "");
  if (digits.length < 8) return null;
  const text = encodeURIComponent(`Hola${u.name ? ` ${u.name.split(" ")[0]}` : ""}, te escribimos de VIVOTaller.`);
  return (
    <a href={`https://wa.me/${digits}?text=${text}`} target="_blank" rel="noopener noreferrer" title={`WhatsApp ${u.phone}`}
      className="grid place-items-center w-9 h-9 rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors shrink-0" data-testid={`admin-user-wa-${u.id}`}>
      <WhatsAppIcon />
    </a>
  );
}

function UserFilters({ q, setQ, from, setFrom, to, setTo, total, onExport, exporting }) {
  const preset = PRESETS.find((p) => p.from() === from && p.to() === to)?.id;
  return (
    <div className="space-y-3">
      <div className="flex flex-col lg:flex-row gap-2 lg:items-end">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o correo…" className="pl-9" data-testid="admin-users-search" />
        </div>
        <div className="grid grid-cols-2 gap-2 lg:w-[340px]">
          <div className="text-xs font-medium space-y-1"><span>Cuenta creada desde</span><DateField value={from} max={to || undefined} onChange={setFrom} testId="admin-users-from" floating /></div>
          <div className="text-xs font-medium space-y-1"><span>Hasta</span><DateField value={to} min={from || undefined} onChange={setTo} testId="admin-users-to" floating /></div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map((p) => (
          <Button key={p.id} size="sm" variant={preset === p.id ? "default" : "outline"} className="rounded-full h-8" onClick={() => { setFrom(p.from()); setTo(p.to()); }} data-testid={`admin-users-preset-${p.id}`}>{p.label}</Button>
        ))}
        {(from || to) && <Button size="sm" variant="ghost" className="h-8" onClick={() => { setFrom(""); setTo(""); }} data-testid="admin-users-clear-dates">Quitar fechas</Button>}
        <span className="text-xs text-muted-foreground ml-auto" data-testid="admin-users-total">{total ?? "…"} usuario{total === 1 ? "" : "s"}</span>
        <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={onExport} disabled={exporting || !total} data-testid="admin-users-export">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" />Descargar CSV</>}
        </Button>
      </div>
    </div>
  );
}

export function AdminUsers() {
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(null);
  const [target, setTarget] = useState(null);
  const [chat, setChat] = useState(null);
  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data, headers } = await api.get("/admin/users/export.csv", { params: { q, date_from: from, date_to: to }, responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || "usuarios-vivotaller.csv";
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`CSV descargado (${total} usuario${total === 1 ? "" : "s"})`);
    } catch { toast.error("No pudimos descargar el CSV. Inténtalo nuevamente."); }
    finally { setExporting(false); }
  };
  const load = () => api.get("/admin/users", { params: { q, date_from: from, date_to: to } }).then((r) => { setRows(r.data.users); setTotal(r.data.total); });
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q, from, to]); // eslint-disable-line
  return (
    <div className="pt-6 space-y-4" data-testid="admin-users">
      <UserFilters q={q} setQ={setQ} from={from} setFrom={setFrom} to={to} setTo={setTo} total={total}
        onExport={exportCsv} exporting={exporting} />
      <Card className="divide-y divide-border">
        {rows === null && <div className="py-8 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}
        {rows?.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">Sin resultados.</p>}
        {rows?.map((u) => (
          <div key={u.id} className="flex items-center gap-3 px-4 py-3" data-testid={`admin-user-${u.id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-medium sm:truncate break-words">{u.name || "Sin nombre"} <Badge variant="outline" className="ml-1 text-[10px]">{u.is_maestro && u.role !== "super_admin" ? "Maestro" : ROLE[u.role] || u.role || "Sin rol"}</Badge>
                {u.is_new && <span className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] font-bold align-middle" data-testid={`admin-user-new-${u.id}`}><Sparkles className="w-3 h-3" />Nuevo</span>}</p>
              <p className="text-xs text-muted-foreground truncate">{u.email}{u.phone ? ` · ${u.phone}` : ""}{u.created_at ? ` · desde ${formatDate(u.created_at)}` : ""}</p>
            </div>
            <WaLink u={u} />
            {u.role !== "super_admin" && (
              <button type="button" onClick={() => setChat(u)} title="Enviar mensaje en VIVOTaller" aria-label="Enviar mensaje"
                className="grid place-items-center w-9 h-9 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors shrink-0" data-testid={`admin-user-msg-${u.id}`}>
                <MessagesSquare className="w-4 h-4" />
              </button>
            )}
            {u.role !== "super_admin" && (
              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive shrink-0" onClick={() => setTarget(u)} data-testid={`admin-user-delete-${u.id}`}>
                <Trash2 className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Eliminar</span>
              </Button>
            )}
          </div>
        ))}
      </Card>
      {target && <DeleteDialog target={target} onClose={() => setTarget(null)} onDeleted={() => { setTarget(null); load(); }} />}
      <Dialog open={!!chat} onOpenChange={(o) => { if (!o) setChat(null); }}>
        <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden" data-testid="admin-chat-dialog">
          <DialogHeader className="px-4 py-3 border-b text-left"><DialogTitle className="text-base">{chat?.name || chat?.email}</DialogTitle><DialogDescription>{chat?.email}</DialogDescription></DialogHeader>
          <div className="h-[60vh] min-h-[380px]">{chat && <AdminChat userId={chat.id} />}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
